// src/supabaseClient.js
// Supabase-to-PHP PHP Adapter
// This replaces the actual Supabase client, intercepting JS SDK calls
// and formatting them as API requests to your new cPanel PHP backend!

// !!! IMPORTANT: CHANGE THIS URL TO YOUR CPANEL DOMAIN !!!
export const API_URL = 'https://cigsida.com/api/api.php';
export const STORAGE_URL = 'https://cigsida.com/api/storage';

class MockQueryBuilder {
  constructor(table) {
    this._table = table;
    this._method = 'select';
    this._columns = '*';    // Raw columns string – may include join syntax like "table!fk (col)"
    this._filters = [];
    this._orderConfig = [];
    this._limitCount = null;
    this._data = null;
    this._isSingle = false;
  }
  
  // Store raw column string – do NOT sanitize here; PHP api.php handles parsing.
  select(cols = '*') { 
    if (this._method !== 'insert' && this._method !== 'update' && this._method !== 'delete') {
      this._method = 'select'; 
    }
    this._columns = cols; 
    return this; 
  }
  insert(data) { this._method = 'insert'; this._data = data; return this; }
  update(data) { this._method = 'update'; this._data = data; return this; }
  delete() { this._method = 'delete'; return this; }
  
  eq(col, val) { this._filters.push(['eq', col, val]); return this; }
  neq(col, val) { this._filters.push(['neq', col, val]); return this; }
  in(col, val) { this._filters.push(['in', col, val]); return this; }
  ilike(col, val) { this._filters.push(['ilike', col, val]); return this; }
  
  order(col, config = { ascending: true }) {
    this._orderConfig.push({ col, asc: config.ascending !== false }); 
    return this; 
  }
  
  limit(n) { this._limitCount = n; return this; }
  single() { this._isSingle = true; return this; }
  
  // Helper to convert 'true'/'false' strings back to booleans
  _convertBooleans(obj) {
    if (Array.isArray(obj)) {
      return obj.map(item => this._convertBooleans(item));
    } else if (obj !== null && typeof obj === 'object') {
      const newObj = {};
      for (const key in obj) {
        let val = obj[key];
        if (val === 'true') val = true;
        else if (val === 'false') val = false;
        newObj[key] = this._convertBooleans(val);
      }
      return newObj;
    }
    return obj;
  }

  // This executes the request when awaited
  async then(resolve, reject) {
    try {
      // Trim the columns string – PHP join parser is whitespace-tolerant
      // but trimming helps avoid accidental leading-newline issues.
      const columnsToSend = typeof this._columns === 'string'
        ? this._columns.trim()
        : this._columns;

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'query',
          table: this._table,
          method: this._method,
          columns: columnsToSend,
          filters: this._filters,
          order: this._orderConfig,
          limit: this._limitCount,
          data: this._data,
          single: this._isSingle
        })
      });
      const result = await response.json();
      if (result.error) {
         console.error(`[SupabaseClient] Error in query for table ${this._table}:`, result.error);
      }
      
      // PHP decodeFromDb() now handles boolean strings and JSON decoding server-side.
      // _convertBooleans is kept as a client-side safety net for any remaining cases.
      if (result.data) {
        result.data = this._convertBooleans(result.data);
      }
      
      resolve(result); // Return the {data, error} packet matching Supabase structure
    } catch(err) {
      console.error(`[SupabaseClient] Fetch Failed:`, err);
      resolve({ data: null, error: err });
    }
  }
}

export const supabase = {
  from: (table) => new MockQueryBuilder(table),
  auth: {
    getSession: async () => {
      const session = localStorage.getItem('cigsida_auth_session');
      return { data: { session: session ? JSON.parse(session) : null }, error: null };
    },
    signInWithPassword: async ({ email, password }) => {
      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'auth_login', email, password })
        });
        const result = await response.json();
        
        if (result.error) return { data: null, error: result.error };
        
        const session = {
          user: result.data.user,
          access_token: result.data.session.access_token,
          expires_at: Math.floor(Date.now() / 1000) + 3600
        };
        
        localStorage.setItem('cigsida_auth_session', JSON.stringify(session));
        return { data: { session, user: session.user }, error: null };
      } catch (err) {
        return { data: null, error: err };
      }
    },
    signOut: async () => {
      localStorage.removeItem('cigsida_auth_session');
      return { error: null };
    },
    onAuthStateChange: (callback) => {
      // Return a basic subscription mock
      return { 
        data: { 
          subscription: { 
            unsubscribe: () => {} 
          } 
        } 
      };
    }
  },
  storage: {
    from: (bucket) => ({
      upload: async (path, file, options={}) => {
        const formData = new FormData();
        formData.append('action', 'storage_upload');
        formData.append('bucket', bucket);
        formData.append('path', path);
        formData.append('file', file);
        
        try {
          const res = await fetch(API_URL, { method: 'POST', body: formData });
          const jsonres = await res.json();
          // Map to standard {data, error} response of supabase
          if (jsonres.error) return { data: null, error: jsonres.error };
          return { data: jsonres, error: null };
        } catch(err) {
          return { data: null, error: err };
        }
      },
      getPublicUrl: (path) => {
         return { data: { publicUrl: `${STORAGE_URL}/${bucket}/${path}` } };
      },
      remove: async (paths) => {
        const formData = new FormData();
        formData.append('action', 'storage_delete');
        formData.append('bucket', bucket);
        formData.append('paths', JSON.stringify(paths));
        
        try {
          const res = await fetch(API_URL, { method: 'POST', body: formData });
          const jsonres = await res.json();
          if (jsonres.error) return { data: null, error: jsonres.error };
          return { data: jsonres, error: null };
        } catch(err) {
          return { data: null, error: err };
        }
      }
    })
  }
};
